import { Icon, Input, Text } from '@ui-kitten/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, View } from 'react-native';
import { useActive, useHover } from 'react-native-web-hooks';

import useResponsiveStyleSheet, {
  createResponsiveStyle,
} from '../../hooks/useResponsiveStyleSheet';
import { getValueById } from '../../utils/helper';

type Data = {
  id: string;
  text: string;
};

type Props = {
  label?: string;
  data: { id: string; text: string }[];
  onSelect: (index: any) => void;
  size?: string;
  status?: string;
  placeholder?: string;
  value: string | undefined;
  initialValue?: string;
  disabled?: boolean;
  marginTop?: number;
  hideCloseIcon?: boolean;
  labelHalf?: boolean;
};

const AutocompleteComponent: React.FC<Props> = ({
  label,
  data,
  onSelect,
  size = 'large',
  status = 'basic',
  placeholder,
  value,
  initialValue,
  disabled = false,
  marginTop = 16,
  hideCloseIcon,
  labelHalf,
}) => {
  const styles = useResponsiveStyleSheet(themedStyles);

  const ref = useRef(null);
  const isHovered = useHover(ref);
  const isActive = useActive(ref);

  const [choices, setChoices] = useState<Data[]>(data);
  const [choiceText, setChoiceText] = useState<string>('');
  const [showIcon, setShowIcon] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const selectContainerRef = useRef<any>(null);

  const onChangeText = (query: string): void => {
    const visibleChoices = data.filter((item) => {
      return item.text.toLowerCase().includes(query.toLowerCase());
    });

    setChoiceText(query);
    setChoices(visibleChoices);
    setShowIcon(true);
    setDropdownOpen(true);
  };

  const clearInput = () => {
    setChoiceText('');
    onSelect('');
    setChoices(data);
    setShowIcon(false);
    setDropdownOpen(false);
  };

  const onSelectItem = (item: Data) => {
    setChoiceText(item.text);
    onSelect(item.id);
    setDropdownOpen(false);
  };

  useEffect(() => {
    setChoices(data);
  }, [data]);

  useEffect(() => {
    if (value === '' || value === null) {
      setChoiceText('');
      setChoices(data);
      setShowIcon(false);
    }
  }, [value]);

  useEffect(() => {
    if (choiceText) {
      setShowIcon(true);
    }
  }, [choiceText]);

  useEffect(() => {
    if (initialValue && value !== '') {
      setChoiceText(initialValue);
    }
    setChoices(choices.sort((a, b) => a.text.localeCompare(b.text)));
  }, []);

  useEffect(() => {
    if (value) {
      const valueText = getValueById(choices, value);
      setChoiceText(valueText);
    }
  }, [value]);

  useEffect(() => {
    if (!dropdownOpen) return;

    const handleScroll = () => {
      measureInput();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [dropdownOpen, choices.length]);

  const measureInput = () => {
    if (!selectContainerRef.current) return;

    const rect = selectContainerRef.current.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const availableBelow = viewportHeight - rect.bottom - 8;
    const availableAbove = rect.top - 8;
    const estimatedDropdownHeight = Math.min(
      200,
      Math.max(1, choices.length) * 44,
    );
    const openUpwards =
      availableBelow < Math.min(estimatedDropdownHeight, 160) &&
      availableAbove > availableBelow;
    const maxHeight = Math.max(
      80,
      Math.min(200, openUpwards ? availableAbove : availableBelow),
    );

    // position: fixed is viewport-relative; do not add scroll offsets.
    setDropdownPosition({
      top: openUpwards
        ? Math.max(8, rect.top - maxHeight - 8)
        : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  };

  const renderCloseIcon = useCallback(
    (props: any) =>
      showIcon && !hideCloseIcon && !disabled ? (
        <Pressable onPress={clearInput}>
          <Icon name="close" {...props} />
        </Pressable>
      ) : (
        <></>
      ),
    [showIcon],
  );

  return (
    <View style={[styles.container, { marginTop: 5 + marginTop }]} ref={ref}>
      {label && (
        <View style={styles.label}>
          {labelHalf && <View style={styles.labelHalf} />}
          <Text
            style={[
              styles.labelText,
              isActive && !disabled
                ? styles.active
                : isHovered && !disabled
                ? styles.hover
                : styles.inactive,
            ]}
          >
            {label}
          </Text>
        </View>
      )}

      <View
        style={[
          styles.selectContainer,
          isActive && !disabled
            ? styles.active
            : isHovered && !disabled
            ? styles.hover
            : styles.inactive,
        ]}
        ref={selectContainerRef}
      >
        <Input
          disabled={disabled}
          status={status}
          size={size}
          placeholder={placeholder}
          value={choiceText}
          accessoryRight={renderCloseIcon}
          onChangeText={onChangeText}
          onFocus={() => {
            if (choices.length > 0) {
              measureInput();
              setDropdownOpen(true);
            }
          }}
          onBlur={() => {
            setTimeout(() => setDropdownOpen(false), 150);
          }}
          style={styles.select}
        />
      </View>

      {dropdownOpen &&
        choices.length > 0 &&
        dropdownPosition &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: dropdownPosition.maxHeight,
              overflowY: 'auto',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 99999,
            }}
          >
            {choices.map((item) => (
              <div
                key={item.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelectItem(item);
                }}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontFamily: 'UniversLTPro_Regular',
                  fontSize: 14,
                  lineHeight: '20px',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  backgroundColor: 'white',
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.backgroundColor =
                    '#f5f5f5')
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.backgroundColor =
                    'white')
                }
              >
                {item.text}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </View>
  );
};

const themedStyles = createResponsiveStyle({
  baseStyle: {
    container: {
      flex: 1,
      position: 'relative',
      paddingTop: 8,
    },
    label: {
      flex: 1,
      position: 'absolute',
      backgroundColor: 'input-field-background',
      left: 7,
      top: -8,
      paddingHorizontal: 5,
      paddingVertical: 0,
      margin: 0,
      zIndex: 1,
    },
    labelHalf: {
      height: '50%',
      backgroundColor: 'input-field-background-focused',
      width: '100%',
      position: 'absolute',
      left: 0,
    },
    active: {
      color: 'input-field-outline-active',
      borderColor: 'input-field-outline-active',
    },
    hover: {
      color: 'input-field-outline-hover',
      borderColor: 'input-field-outline-hover',
    },
    inactive: {
      color: 'text-grey',
      borderColor: 'input-field-outline',
    },
    labelText: {
      fontSize: 14,
      fontFamily: 'UniversLTPro_Regular',
      color: 'input-field-outline',
      zIndex: 2,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    select: {
      alignSelf: 'stretch',
      overflow: 'hidden',
    },
    selectContainer: {
      alignSelf: 'stretch',
      borderRadius: 3,
      borderWidth: 1,
      backgroundColor: 'input-field-background',
      color: 'text-grey',
      borderColor: 'input-field-outline',
      overflow: 'visible',
      zIndex: 1,
    },
    dropdownPortal: {
      position: 'fixed',
      backgroundColor: 'input-field-background',
      marginTop: 4,
      maxHeight: 200,
      borderRadius: 4,
      borderColor: 'input-field-outline',
      borderWidth: 1,
      zIndex: 99999,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    } as any,
    dropdown: {
      maxHeight: 200,
    },
  },
});

export default AutocompleteComponent;
